import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { LoggerService } from './logging/logger.service';
import { catchError, map } from 'rxjs/operators';
import { Drive } from '../insurgo-interfaces/drive';
import { Observable, of } from 'rxjs';
import { Hardware } from '../insurgo-interfaces/hardware.interface';
import { ErrorHandlingService } from './error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class DriveHelperService {

  apiUrl = environment.apiUrl;

  constructor(
    public authHttp: HttpClient,
    private readonly logger: LoggerService,
    private errorHandlingService: ErrorHandlingService
  ) { }


  getDriveFromApData(apData: string): Observable<Drive> {
    let driveSerial = '';

    apData = apData.replace(/\s+/g, ''); // remove any spaces
    // does it contain "KIT:ERASED"
    if (apData.includes('KIT:ERASED')) {
      // get the drive serial
      driveSerial = apData.substring(13, 25);
      // get drive info  
    } else if (apData.includes('SWAT')) {
      driveSerial = apData.substring(14, 26);
    } else if (apData.includes('K2(E):') || apData.includes('K2(S):')) {
      driveSerial = apData.substring(apData.indexOf(':') + 1, apData.indexOf(';'));
    } else {
      driveSerial = 'invalid';
      return of({
        type: 'drive',
        driveSerial: driveSerial,
        valid: false
      })
    }


    return this.getDrive(driveSerial);
  }

  getDrive(driveSerial: string | undefined): Observable<Drive> {

    if (!driveSerial) {
      let _fakeDrive: Drive = {
        type: 'drive',
        driveSerial: 'invalid',
        valid: false
      }
      return of(_fakeDrive);
    }

    let URL = `${this.apiUrl}/facility/drive/report/${driveSerial}`;
    return this.authHttp.get<Drive[]>(URL)
      .pipe(
        map((drives) => {
          if (drives.length > 0) {
            return drives[0];
          } else {
            // drive doesn't exists return empty invalid drive
            let _fakeDrive: Drive = {
              type: 'drive',
              driveSerial: driveSerial,
              valid: false
            }
            return _fakeDrive;
          }
        }),
        catchError((error) => {
          return this.errorHandlingService.handleError(error);
        }),
      );
  }


  getAllDrives(): Observable<Drive[]> {
    let URL = `${this.apiUrl}/facility/drive/report`;

    return this.authHttp.get<Drive[]>(URL)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError(error);
        }),
      );
  }

  saveNewKitDrive(driveSerial, driveLocationID): Observable<number> {
    const URL = `${this.apiUrl}/facility/drive/`;
    var _data = {
      driveSerial: driveSerial,
      driveName: 'unknown',
      driveLocationID: driveLocationID
    }
    return this.authHttp.post<number>(URL, _data)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError(error);
        }),
      );
  }

  activateDrive(_driveJWT): Observable<boolean> {
    const URL = `${this.apiUrl}/facility/drive/activate`;
    let _data = {
      driveJWT: _driveJWT
    }
    return this.authHttp.post<boolean>(URL, _data)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleError(error);
        }),
      );
  }

  isKITDriveValid(driveSerial: string, tapeID: number): Observable<undefined | {
    HardwareRegisterID: number;
    AssetTag: string;
    SerialNumber: string;
    DriveName: string;
  }> {
    const URL = `${this.apiUrl}/drive/kit-check`;
    let params = new HttpParams();
    params = params.append('driveSerial', driveSerial);
    params = params.append('tapeID', tapeID.toString());

    return this.authHttp.get<undefined | {
      HardwareRegisterID: number;
      AssetTag: string;
      SerialNumber: string;
      DriveName: string;
    }>(URL, { params: params })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleErrorTrustedRoute(error);
        }),
      );
  }

  isSWATDriveValid(driveSerial: string, tapeID: number): Observable<undefined | {
    HardwareRegisterID: number;
    AssetTag: string;
    SerialNumber: string;
    DriveName: string;
  }> {
    const URL = `${this.apiUrl}/drive/swat-check`;
    let params = new HttpParams();
    params = params.append('driveSerial', driveSerial);
    params = params.append('tapeID', tapeID.toString());

    return this.authHttp.get<undefined | {
      HardwareRegisterID: number;
      AssetTag: string;
      SerialNumber: string;
      DriveName: string;
    }>(URL, { params: params })
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleErrorTrustedRoute(error);
        }),
      );
  }

  getAllHardwareOnSite(): Observable<Hardware[]> {
    let URL = `${this.apiUrl}/hardware/on-site`;

    return this.authHttp.get<Hardware[]>(URL)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleErrorTrustedRoute(error);
        }),
      );
  }

  getArrivingHardware(): Observable<Hardware[]> {
    let URL = `${this.apiUrl}/hardware/arriving`;

    return this.authHttp.get<Hardware[]>(URL)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleErrorTrustedRoute(error);
        }),
      );
  }

  markHardwareAsArrived(arrDriveIDs: number[], arrScannerIDs: number[], arrivalDateSec: number): Observable<{ success: Hardware[], errors: Hardware[] }> {
    const body = {
      arrDriveHardwareIDs: arrDriveIDs,
      arrScannerHardwareIDs: arrScannerIDs,
      arrivedDateSec: arrivalDateSec
    }
    const URL = `${this.apiUrl}/hardware/mark-arrived`;

    return this.authHttp.post<{ success: Hardware[], errors: Hardware[] }>(URL, body)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleErrorTrustedRoute(error);
        }),
      );
  }

  markHardwareAsDispatched(arrDriveIDs: number[], arrScannerIDs: number[], dispatchDateSec: number,
    dispatchReference: string
  ): Observable<{ success: Hardware[], errors: Hardware[] }> {
    const body = {
      arrDriveHardwareIDs: arrDriveIDs,
      arrScannerHardwareIDs: arrScannerIDs,
      dispatchDateSec: dispatchDateSec,
      dispatchReference: dispatchReference
    }
    const URL = `${this.apiUrl}/hardware/mark-dispatched`;

    return this.authHttp.post<{ success: Hardware[], errors: Hardware[] }>(URL, body)
      .pipe(
        catchError((error) => {
          return this.errorHandlingService.handleErrorTrustedRoute(error);
        }),
      );
  }
}
